import { DynamicElementMixin } from "%/dynamic-components/render-engine/mixins/production/dynamic-element-mixin";
import LayoutWidthHeightOptions from "%/dynamic-components/render-engine/bundles/layout-width-height-options";
import PositioningOptions from "%/dynamic-components/render-engine/bundles/positioning-options";
import AppearanceOptions from "%/dynamic-components/render-engine/bundles/appearance-options";
import SpacingOptions from "%/dynamic-components/render-engine/bundles/spacing-options";
import BorderOptions from "%/dynamic-components/render-engine/bundles/border-options";
import BackgroundOptions from "%/dynamic-components/render-engine/bundles/background-options";
import BackgroundColor from "%/dynamic-components/render-engine/options/background-options/background-color";
import BaseOption from "%/dynamic-components/render-engine/classes/base-option";
import BackgroundImage from "%/dynamic-components/render-engine/options/background-options/background-image";
import LayoutDirection from "%/dynamic-components/render-engine/options/layout-options/layout-direction";
import LayoutHeight from "%/dynamic-components/render-engine/options/layout-options/layout-height";
import LayoutWidth from "%/dynamic-components/render-engine/options/layout-options/layout-width";
import LayoutGap from "%/dynamic-components/render-engine/options/layout-options/layout-gap";
import normalizeGapValue from "%/dynamic-component-editor/helpers/gap-style-value";
import Padding, {
  PADDING_DEFAULT_EMPTY_VALUE,
} from "%/dynamic-components/render-engine/options/spacing-options/padding";
import Margins, {
  MARGIN_DEFAULT_EMPTY_VALUE,
} from "%/dynamic-components/render-engine/options/spacing-options/margins";
import BorderRadius from "%/dynamic-components/render-engine/options/border-options/border-radius";
import MaxWidth from "%/dynamic-components/render-engine/options/spacing-options/max-width";
import MaxHeight from "%/dynamic-components/render-engine/options/spacing-options/max-height";
import MinWidth from "%/dynamic-components/render-engine/options/spacing-options/min-width";
import MinHeight from "%/dynamic-components/render-engine/options/spacing-options/min-height";
import BorderSides from "%/dynamic-components/render-engine/options/border-options/border-sides";
import ShowDots from "%/dynamic-components/render-engine/options/carousel/show-dots";
import ShowArrows from "%/dynamic-components/render-engine/options/carousel/show-arrows";
import Snap from "%/dynamic-components/render-engine/options/carousel/snap";
import CarouselOptions from "%/dynamic-components/render-engine/bundles/carousel-options";
import ItemMinWidth from "%/dynamic-components/render-engine/options/carousel/item-min-width";
import ItemMaxWidth from "%/dynamic-components/render-engine/options/carousel/item-max-width";
import AnimationOptions from "%/dynamic-components/render-engine/bundles/animations-options";
import AutoScroll from "%/dynamic-components/render-engine/options/carousel/auto-scroll";
import ScrollSpeed from "%/dynamic-components/render-engine/options/carousel/scroll-speed";
import InfiniteScroll from "%/dynamic-components/render-engine/options/carousel/infinite-scroll";
import ItemsPadding from "%/dynamic-components/render-engine/options/carousel/items-padding";
import AnimationEffectsOptions from "%/dynamic-components/render-engine/bundles/animation-effects-options";
import {
  OptionMediaResolverMixin,
} from "%/dynamic-components/render-engine/mixins/production/option-media-resolver-mixin";

const CarouselMixinProduction = {
  mixins: [DynamicElementMixin, OptionMediaResolverMixin],
  elementPanelSettings: {
    displayIcon: "bx:carousel",
    displayName: "Carousel",
    elementGroup: "layout",
    elementType: "basic",
  },
  optionBundles: [
    CarouselOptions,
    new LayoutWidthHeightOptions({
      "default.layout-height": 400,
      "default.layout-width": "fill",
    }),
    PositioningOptions,
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    AnimationOptions,
    AnimationEffectsOptions,
    SpacingOptions,
    BorderOptions,
    new BackgroundOptions({}, {
      "background-image": true,
    }),
  ],
  computed: {
    rootProps() {
      return {
        height: this.myHeight,
        maxWidth: this.myMaxWidth,
        minWidth: this.myMinWidth,
        maxHeight: this.myMaxHeight,
        minHeight: this.myMinHeight,
        styleOverrides: {
          borderRadius: this.myBorderRadius,
          backgroundColor: this.getOptionValue(BackgroundColor),
          hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
        },
        borderTop: this.myBorderSides.top,
        borderBottom: this.myBorderSides.bottom,
        borderLeft: this.myBorderSides.left,
        borderRight: this.myBorderSides.right,
        width: this.myWidth,
      };
    },
    showDots() {
      return this.getOptionValue(ShowDots);
    },
    showArrows() {
      return this.getOptionValue(ShowArrows);
    },
    snap() {
      return this.getOptionValue(Snap);
    },
    myBackgroundImage() {
      return this.resolveOptionMedia(this.getOptionValue(BackgroundImage));
    },
    layoutDirection() {
      return this.getOptionValue(LayoutDirection);
    },
    myHeight() {
      return this.getOptionValue(LayoutHeight);
    },
    myWidth() {
      return this.getOptionValue(LayoutWidth);
    },
    myGap() {
      return normalizeGapValue(this.getOptionValue(LayoutGap));
    },
    myItemsPadding() {
      let optionValue = this.getOptionValue(ItemsPadding);

      if (!optionValue) {
        optionValue = 0;
      }

      return optionValue + "px";
    },
    myPadding() {
      let paddingValues = this.getOptionValue(Padding, BaseOption.$states.DEFAULT, null);
      const paddingKeys = Object.keys(paddingValues);
      paddingKeys.forEach(paddingKey => {
        const paddingVal = paddingValues[paddingKey];
        if (paddingVal === null) {
          paddingValues[paddingKey] = PADDING_DEFAULT_EMPTY_VALUE;
        }
      });
      return paddingValues;
    },
    myMargins() {
      let marginValues = this.getOptionValue(Margins, BaseOption.$states.DEFAULT, null);
      const marginKeys = Object.keys(marginValues);
      marginKeys.forEach(marginKey => {
        const marginVal = marginValues[marginKey];
        if (marginVal === null) {
          marginValues[marginKey] = MARGIN_DEFAULT_EMPTY_VALUE;
        }
      });
      return marginValues;
    },
    carouselAnimations() {
      if (this.editMode || !this.myAutoScroll) {
        return [];
      }

      return [
        {
          effect: "scroll-x",
          options: {
            duration: this.myScrollSpeed * 2.5 * 1000,
          },
          target: ".i-carousel-scroller",
          onAction: "page_load",
        },
      ];
    },
    myAutoScroll() {
      return this.getOptionValue(AutoScroll);
    },
    myInfiniteScroll() {
      return this.getOptionValue(InfiniteScroll);
    },
    myScrollSpeed() {
      return this.getOptionValue(ScrollSpeed);
    },
    myBorderRadius() {
      return this.getOptionValue(BorderRadius);
    },
    myMaxWidth() {
      return this.getOptionValue(MaxWidth);
    },
    myMaxHeight() {
      return this.getOptionValue(MaxHeight);
    },
    myMinWidth() {
      return this.getOptionValue(MinWidth);
    },
    myMinHeight() {
      return this.getOptionValue(MinHeight);
    },
    myBorderSides() {
      return this.getOptionValue(BorderSides);
    },
    itemMinWidth() {
      return this.getOptionValue(ItemMinWidth);
    },
    itemMaxWidth() {
      return this.getOptionValue(ItemMaxWidth);
    },
  },
};

export default CarouselMixinProduction;
